/**
 * LiveClassJoinScreen
 * -------------------
 * When a student's class goes live, everything else steps aside: one card,
 * one button. It replaces the banner that used to sit over the top of every
 * page, covering the header and competing with the dashboard for attention
 * exactly when the student had one thing to do.
 *
 * Deliberately no close button: the class is happening now. Once joined, the
 * card waits for the teacher; when a lesson is live, ClassLaunchRouter (which
 * owns navigation) takes the student into the player.
 */

import { useEffect, useRef, useState } from 'react';
import { FaChalkboardTeacher, FaUsers } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { useClassSession } from '../../contexts/ClassSessionContext';
import { useLiveClassSessions } from '../../hooks/useLiveClassSessions';
import { studentLiveState } from '../../lib/classroom/studentLiveState';

interface InviteInfo {
  teacherName: string;
  className: string;
  subject?: string;
  lessonLive: boolean;
}

interface CardProps {
  state: 'invite' | 'waiting' | 'opening';
  invite?: InviteInfo | null;
  joining?: boolean;
  onJoin?: () => void;
}

/** The card itself, from plain props: what the student sees in each state. */
export const LiveClassJoinCard = ({ state, invite, joining = false, onJoin }: CardProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (state === 'invite') buttonRef.current?.focus();
  }, [state]);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-background/95 p-4 backdrop-blur-md">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="live-class-title"
        className="w-full max-w-md rounded-3xl border border-border bg-card p-8 text-center shadow-2xl"
      >
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-teal-400/15 text-teal-400">
          {state === 'invite' ? <FaChalkboardTeacher className="h-7 w-7" /> : <FaUsers className="h-7 w-7 animate-pulse" />}
        </span>

        {state === 'invite' && invite ? (
          <>
            <p className="mt-5 text-xs font-semibold uppercase tracking-[0.16em] text-teal-400">Your class is live</p>
            <h1 id="live-class-title" className="mt-2 text-2xl font-semibold text-foreground">
              {invite.teacherName} started {invite.className}
            </h1>
            {(invite.lessonLive || invite.subject) && (
              <p className="mt-2 text-sm text-muted-foreground">
                {invite.subject ? `${invite.subject} lesson` : 'The lesson'}
                {invite.lessonLive ? ' is in progress' : ' is about to start'}
              </p>
            )}
            <button
              ref={buttonRef}
              type="button"
              onClick={onJoin}
              disabled={joining}
              className="mt-8 w-full rounded-2xl bg-primary px-6 py-4 text-lg font-semibold text-primary-foreground shadow-lg transition hover:opacity-90 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/40 disabled:opacity-60"
            >
              {joining ? 'Joining…' : 'Join class'}
            </button>
          </>
        ) : (
          <>
            <h1 id="live-class-title" className="mt-5 text-2xl font-semibold text-foreground">
              {state === 'opening' ? 'Opening the lesson…' : 'You’re in!'}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground" aria-live="polite">
              {state === 'opening' ? 'Taking you into the lesson now.' : 'Waiting for your teacher to start the lesson…'}
            </p>
            <div className="mx-auto mt-8 flex w-fit gap-1.5" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="h-2.5 w-2.5 animate-bounce rounded-full bg-teal-400"
                  style={{ animationDelay: `${i * 150}ms` }}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/** For students, mounted app-wide (App.jsx): the card whenever their class is live. */
export const LiveClassJoinScreen = () => {
  const { joinedSession, joinSession, sessionLoading } = useClassSession();
  const { sessions } = useLiveClassSessions('my-classes');
  const [joining, setJoining] = useState(false);

  const state = studentLiveState(joinedSession, sessions);

  // Nothing behind the card scrolls while it is up.
  useEffect(() => {
    if (state.kind === 'none') return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [state.kind]);

  if (state.kind === 'none') return null;

  const invite = state.kind === 'invite' ? state.session : null;
  const handleJoin = async () => {
    if (!invite) return;
    setJoining(true);
    try {
      const ok = await joinSession(invite.session_code);
      if (!ok) toast.error('Could not join the class. Try again.');
    } finally {
      setJoining(false);
    }
  };

  return (
    <LiveClassJoinCard
      state={state.kind}
      invite={
        invite
          ? {
              teacherName: invite.teacherName,
              className: invite.className,
              subject: invite.launched_lesson?.subject,
              lessonLive: invite.lessonLive,
            }
          : null
      }
      joining={joining || sessionLoading}
      onJoin={handleJoin}
    />
  );
};
